import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PublicOnlyRoute from './components/PublicOnlyRoute';
import AdminRoute from './components/AdminRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Landing from './pages/Landing';
import { Login, Signup } from './pages/Auth';
import Profile from './pages/Profile';
import ChatHistory from './pages/ChatHistory';
import SettingsInfo from './pages/SettingsInfo';
import SettingsKpis from './pages/SettingsKpis';
import Docs from './pages/Docs';
import './styles/globals.css';

// The Chat workspace and Docs are full-height, app-like views that manage
// their own scroll/footer-free layouts — the marketing/content pages
// (Landing, Settings, auth) keep the standard footer.
const NO_FOOTER_PREFIXES = ['/chat', '/docs', '/projects'];

const ConditionalFooter = () => {
  const location = useLocation();
  if (NO_FOOTER_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;
  return <Footer />;
};

/**
 * Route map — MarginPro is in development, so the whole app is gated
 * behind authentication except the login and signup pages:
 *
 *   /login, /signup   — public (PublicOnlyRoute redirects away if
 *                        already logged in)
 *   everything else   — protected (ProtectedRoute redirects to /login
 *                        if not logged in)
 *   /docs             — protected AND admin-only (AdminRoute)
 *
 * Self-service signup is live (backend ALLOW_SIGNUP=true — see
 * controllers/authController.js). Accounts can still also be
 * provisioned with `npm run seed`.
 *
 * /chat is the primary authenticated view (chat-first workspace).
 * /projects/* renders the same workspace with a project selected:
 *   /projects                          — project list in the center pane
 *   /projects/:projectId               — project detail (chats + composer)
 *   /projects/:projectId/chat/:sessionId — a chat within the project
 * /profile redirects to /chat for anyone with the old URL bookmarked.
 */
const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <Navbar />
      <Routes>
        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<Landing />} />
          <Route path="/chat" element={<Profile />} />
          <Route path="/chat/history" element={<ChatHistory />} />
          <Route path="/chat/:sessionId" element={<Profile />} />
          <Route path="/projects" element={<Profile />} />
          <Route path="/projects/:projectId" element={<Profile />} />
          <Route path="/projects/:projectId/chat/:sessionId" element={<Profile />} />
          <Route path="/settings/info" element={<SettingsInfo />} />
          <Route path="/settings/kpis" element={<SettingsKpis />} />

          <Route element={<AdminRoute />}>
            <Route path="/docs" element={<Docs />} />
          </Route>
        </Route>

        <Route path="/profile" element={<Navigate to="/chat" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ConditionalFooter />
    </BrowserRouter>
  </AuthProvider>
);

export default App;
